import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Phone, CheckCircle2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { addEnquiry, resetEnquiry } from '../../slice/enquirySlice';
import { useTranslation } from "react-i18next";

const BookingPopup = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { success: submitted, loading: submitting } = useSelector((state) => state.enquiry);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    travelers: '',
    arrivalDate: '',
    requirements: ''
  });
  
  const [errors, setErrors] = useState({});

  const today = new Date().toISOString().split('T')[0];

  // Robust Scroll Lock
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll';
      document.body.style.overscrollBehavior = 'none';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
      document.body.style.overscrollBehavior = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
      document.body.style.overscrollBehavior = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (submitted) {
      // Clear local form immediately
      setForm({ name: '', phone: '', email: '', city: '', travelers: '', arrivalDate: '', requirements: '' });
      
      const timer = setTimeout(() => {
        onClose();
        dispatch(resetEnquiry());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitted, dispatch, onClose]);

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t('contact_error_required');
    
    if (!form.phone.trim()) newErrors.phone = t('contact_error_required');
    else if (!/^\d{10}$/.test(form.phone)) newErrors.phone = t('contact_error_invalid_phone');
    
    if (!form.email.trim()) newErrors.email = t('contact_error_required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = t('contact_error_invalid_email');

    if (!form.city.trim()) newErrors.city = t('contact_error_required');
    if (!form.travelers.trim()) newErrors.travelers = t('contact_error_required');
    if (!form.arrivalDate.trim()) newErrors.arrivalDate = t('contact_error_required');
    if (!form.requirements.trim()) newErrors.requirements = t('contact_error_required');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: form.name,
      email: form.email,
      number: form.phone,
      city: form.city,
      travelers: parseInt(form.travelers),
      travelDate: form.arrivalDate,
      message: ` ${form.requirements}`
    };

    dispatch(addEnquiry(payload));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100000] overflow-y-auto flex items-center justify-center p-4">
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Popup Container */}
          <div className="relative w-full flex justify-center items-center pointer-events-none min-h-screen my-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 40 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="relative w-full max-w-[950px] bg-white rounded-[2rem] shadow-2xl overflow-hidden flex flex-col md:flex-row border border-gray-100 pointer-events-auto"
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/10 backdrop-blur-md hover:bg-orange-main hover:text-white flex items-center justify-center text-gray-600 transition-all duration-300 shadow-lg"
              >
                <X size={20} />
              </button>

              {/* Left – Image */}
              <div className="w-full md:w-2/5 relative h-48 md:h-auto min-h-[250px] md:min-h-full">
                <img
                  src="https://media.istockphoto.com/id/177249944/photo/king-and-lion-fight-statue-kandariya-mahadev-temple.jpg?s=612x612&w=0&k=20&c=nBowCup6IKaA6m1oSfqTpz8YDQ6f2HzRS0XeMvlyLm0="
                  alt={t('booking_popup_img_alt')}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="inline-flex items-center gap-2 rounded-full bg-orange-main/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] mb-3 shadow-lg text-white">
                    <Sparkles size={12} />
                    {t('booking_popup_badge')}
                  </div>
                  <h3 className="text-xl md:text-2xl font-black leading-tight text-white">
                    {t('booking_popup_heading')}
                  </h3>
                </div>
              </div>

              {/* Right – Form */}
              <div className="w-full md:w-3/5 px-6 py-8 md:px-8 flex flex-col justify-center bg-white relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-main/5 rounded-full blur-3xl pointer-events-none" />

                <div className="mb-6">
                  <h3 className="text-2xl font-black text-gray-900 leading-tight tracking-tight">
                    {t('booking_popup_form_title')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1.5 font-medium">
                    {t('booking_popup_form_desc')}
                  </p>
                </div>

                <AnimatePresence mode="wait">
                  {!submitted ? (
                    <motion.form
                      key="form"
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0, y: -10 }}
                      onSubmit={handleSubmit}
                      className="space-y-3.5 relative z-10"
                    >
                      {/* Row 1: Name & Email */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div className="space-y-1">
                          <input
                            type="text"
                            placeholder={t('booking_popup_name')}
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className={`w-full border rounded-xl px-4 py-3 text-sm transition-all outline-none ${
                              errors.name ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:ring-2 focus:ring-orange-main/20 focus:border-orange-main hover:border-gray-300 bg-gray-50/50'
                            }`}
                          />
                          {errors.name && <p className="text-[10px] text-red-500 font-bold ml-1">{errors.name}</p>}
                        </div>
                        <div className="space-y-1">
                          <input
                            type="email"
                            placeholder={t('booking_popup_email')}
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className={`w-full border rounded-xl px-4 py-3 text-sm transition-all outline-none ${
                              errors.email ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:ring-2 focus:ring-orange-main/20 focus:border-orange-main hover:border-gray-300 bg-gray-50/50'
                            }`}
                          />
                          {errors.email && <p className="text-[10px] text-red-500 font-bold ml-1">{errors.email}</p>}
                        </div>
                      </div>

                      {/* Row 2: Phone & City */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div className="space-y-1">
                          <div className="flex">
                            <span className="bg-gray-100 border border-r-0 border-gray-200 rounded-l-xl px-3 py-3 text-xs font-bold text-gray-500 flex items-center">
                              🇮🇳 +91
                            </span>
                            <input
                              type="tel"
                              placeholder={t('booking_popup_phone')}
                              value={form.phone}
                              onChange={(e) =>
                                setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })
                              }
                              className={`w-full border rounded-r-xl px-3 py-3 text-sm transition-all outline-none ${
                                errors.phone ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:ring-2 focus:ring-orange-main/20 focus:border-orange-main hover:border-gray-300 bg-gray-50/50'
                              }`}
                            />
                          </div>
                          {errors.phone && <p className="text-[10px] text-red-500 font-bold ml-1">{errors.phone}</p>}
                        </div>
                        <div className="space-y-1">
                          <input
                            type="text"
                            placeholder={t('booking_popup_city')}
                            value={form.city}
                            onChange={(e) => setForm({ ...form, city: e.target.value })}
                            className={`w-full border rounded-xl px-4 py-3 text-sm transition-all outline-none ${
                              errors.city ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:ring-2 focus:ring-orange-main/20 focus:border-orange-main hover:border-gray-300 bg-gray-50/50'
                            }`}
                          />
                          {errors.city && <p className="text-[10px] text-red-500 font-bold ml-1">{errors.city}</p>}
                        </div>
                      </div>

                      {/* Row 3: Travelers & Arrival Date */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        <div className="space-y-1">
                          <input
                            type="number"
                            placeholder={t('booking_popup_travelers')}
                            value={form.travelers}
                            min="1"
                            onChange={(e) => setForm({ ...form, travelers: e.target.value })}
                            className={`w-full border rounded-xl px-4 py-3 text-sm transition-all outline-none ${
                              errors.travelers ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:ring-2 focus:ring-orange-main/20 focus:border-orange-main hover:border-gray-300 bg-gray-50/50'
                            }`}
                          />
                          {errors.travelers && <p className="text-[10px] text-red-500 font-bold ml-1">{errors.travelers}</p>}
                        </div>
                        <div className="space-y-1">
                          <input
                            type="date"
                            min={today}
                            value={form.arrivalDate}
                            onChange={(e) => setForm({ ...form, arrivalDate: e.target.value })}
                            className={`w-full border rounded-xl px-4 py-3 text-sm transition-all outline-none text-gray-700 ${
                              errors.arrivalDate ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:ring-2 focus:ring-orange-main/20 focus:border-orange-main hover:border-gray-300 bg-gray-50/50'
                            }`}
                          />
                          {errors.arrivalDate && <p className="text-[10px] text-red-500 font-bold ml-1">{errors.arrivalDate}</p>}
                        </div>
                      </div>

                      {/* Row 4: Requirements */}
                      <div className="space-y-1">
                        <textarea
                          placeholder={t('booking_popup_requirements')}
                          value={form.requirements}
                          onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                          rows={3}
                          className={`w-full border rounded-xl px-4 py-3 text-sm transition-all outline-none resize-none ${
                            errors.requirements ? 'border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:ring-2 focus:ring-orange-main/20 focus:border-orange-main hover:border-gray-300 bg-gray-50/50'
                          }`}
                        />
                        {errors.requirements && <p className="text-[10px] text-red-500 font-bold ml-1">{errors.requirements}</p>}
                      </div>

                      {/* Submit Button */}
                      <motion.button
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={submitting}
                        className="w-full sm:w-[60%] bg-orange-main text-white font-black text-[14px] py-3.5 rounded-xl shadow-lg hover:bg-orange-600 transition-all flex items-center justify-center gap-2 uppercase tracking-widest mt-2"
                      >
                        <Phone size={16} />
                        {submitting ? t('booking_popup_submitting') : t('booking_popup_submit')}
                      </motion.button>
                    </motion.form>
                  ) : (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12 relative z-10"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
                        className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-5 shadow-lg"
                      >
                        <CheckCircle2 size={40} className="text-white" />
                      </motion.div>
                      <h4 className="text-2xl font-black text-gray-900 mb-2">{t('booking_popup_success_title')}</h4>
                      <p className="text-sm text-gray-500 max-w-[250px] mx-auto">
                        {t('booking_popup_success_msg')}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default BookingPopup;
